import { Message, Conversation } from '@twilio/conversations';

export type LoginPayload = { email: string; password: string };
export type LogoutPayload = { installationId: string };

export type LoginFirebasePayload = {
  firebaseToken: string;
  bambuserOrgId: string;
  bambuserUserId: string;
  retailerId: string;
  regionId: string;
};

export type RegisterAssociatePayload = {
  code: string;
  email: string;
  password: string;
  passwordConfirmation: string;
};

export type UpdatePasswordFormFieldValues = {
  password: string;
  confirmPassword: string;
};

export type RecoveryCodeFormFieldValues = {
  code: string;
};

export type AuthenticateEmailResponse = {
  authV2: boolean;
  registered: boolean;
  sso: boolean;
  valid: boolean;
};

export enum OnboardingSteps {
  accountCreation = 'accountCreation',
  profileSetup = 'profileSetup',
  introductionSplash = 'introductionSplash',
  introductionSplashSkipped = 'introductionSplashSkipped',
  introduction = 'introduction',
  introductionSkipped = 'introductionSkipped',
  introductionComplete = 'introductionComplete',
  demoChat = 'demoChat',
  demoChatSkipped = 'demoChatSkipped',
  approval = 'approval',
  letsbegin = 'letsbegin',
  onboard = 'onboard',
}

export enum ProfileStepScreenName {
  fullName = 'fullname',
  languages = 'languages',
  profilePicture = 'profilepicture',
  employeeId = 'employeeid',
  storeSelector = 'store-selector',
}

export interface ProfileStep {
  copy?: {
    fieldName: string;
    subtitle: string;
    title: string;
  };
  screenName: ProfileStepScreenName;
}

export interface AssociateSetupResponse {
  profileSteps: ProfileStep[];
  retailer: {
    id: string;
    name: string;
  };
  retailerId: string;
  introductionURL: string;
  faqURL: string;
  onboarding: {
    elearning: boolean;
    current: OnboardingSteps;
    next: OnboardingSteps;
    autoApprove: boolean;
  };
}

export enum IntroModuleStaticType {
  introVideo = 'intro-video',
  image = 'image',
  privacy = 'privacy',
  loopingVideo = 'looping-video',
}

export type IntroModule = {
  _id: string;
  mediaUrl: string;
  placeHolderUrl: string;
  type: IntroModuleStaticType;
  title: string;
  body: string;
  subtitleUrl?: string;
  showSkipAlert?: boolean;
};

export type CompleteAssociateSetupStepPayload = {
  code: string;
  email: string;
  password: string;
  passwordConfirmation: string;
};

export type AuthenticateInviteCodeResponse = {
  introductionURL: string;
  profileSteps: ProfileStep[];
  retailer: {
    id: string;
    name: string;
  };
  retailerId: string;
  sso: boolean;
  valid: boolean;
};

export type Store = {
  _id: string;
  id: string;
  publicName: string;
  name: string;
  type: 'store';
  location: [number, number];
  active: boolean;
  address: string;
};

export type LoginFormFieldValues = {
  email: string;
  password: string;
};

export enum AssociateApprovalStatus {
  Approved = 'approved',
  Pending = 'pending',
  Declined = 'deactivated',
}

export type AssociateSettingsBackendResponse = {
  manager: boolean;
  clientVersion: {
    version: string;
    optional: boolean;
  };
  settings: {
    activityFeed: boolean;
    archive: boolean;
    assistant: boolean;
    audioCall: boolean;
    barcode: boolean;
    countdown: boolean;
    currency: string;
    customerSupportEnabled: boolean;
    getNotified: boolean;
    localisedFeatures: {
      proTips: boolean;
      photoTagging: boolean;
    };
    queue: boolean;
    requestTimer: number;
    salesforceLiveAgent: boolean;
    showInstagramLink: boolean;
    showRatingsCell: boolean;
    twoWayVideoCall: boolean;
    contacts: boolean;
    catalog: boolean;
    endOfChatRating: boolean;
    deliveryChannelSms: boolean;
    deliveryChannelWhatsapp: boolean;
    deliveryChannelEmail: boolean;
    contentFilterExplicitMedia: boolean;
    maximumChats: number;
    emojiReactions: string[];
    shoppableStories: boolean;
    primaryMerchantChannel: string;
  };
  onboarding: {
    elearning: boolean;
    faqURL: string;
  };
  outbound: {
    quota: number;
  };
  lastSaleDate: string;
  lastSaleConversationId: string;
  outOfOffice: boolean;
  merchants: string[];
  conversationId: string;
};

export type AssociateInsight = {
  time: string;
  date: string;
  chats: number;
  sales: number;
  value: string;
};

export type Post = {
  _id: string;
  title: string;
  body: string;
  author: {
    avatar: string;
    name: string;
  };
  media: {
    type: string;
    name: string;
    url: string;
  }[];
  publishedAt: string | null;
  createdAt: string;
  modifiedAt: string;
  unpublishAt: string | null;
  retailer: string;
  status: 'published' | 'unpublished' | 'draft';
  analytics: {
    deliveries: number;
    uniqueDeliveries: number;
    deliveredTo: string[];
    reads: number;
    uniqueReads: number;
    readBy: string[];
  };
};

export interface IntroModuleAsPost {
  _id: string;
  mediaUrl: string;
  placeHolderUrl: string;
  type: IntroModuleStaticType;
  title: string;
  body: string;
  author: {
    name: string;
    avatar: string;
  };
  publishDate: string;
  isRead: boolean;
  id: number;
}

export type Department = {
  name: string;
  _id: string;
};

export type AssociateFilter = {
  type: 'department';
  tags: [
    {
      available: boolean;
      id: string;
      name: string;
      selected: boolean;
      type: 'department';
    },
  ];
};

export type UpdateAssociatePayload = {
  firstName?: string;
  lastName?: string;
  spokenLanguages?: string[];
  employeeId?: string;
  store?: string; // Store ID
  phoneNumber?: string;
  email?: string;
  picture?: {
    base64?: string; // Base64-encoded image string
    fileType?: string; // MIME type of the image (e.g., "image/png")
  };
  browserNotifications?: {
    allowShow?: boolean
  };
};

export type AssociateFeatures = {
  browserNotifications: {
    allowShow: boolean;
  };
};

export type Associate = {
  id: string;
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  image?: string;
  username: string;
  retailer: string;
  available: boolean;
  storeId: string;
  approvalStatus: AssociateApprovalStatus;
  departments: Department[];
  employeeId?: string;
  features?: AssociateFeatures;
  spokenLanguages: string[];
};

export type SavedReplyType = 'all' | 'team' | 'custom';

export enum ChatType {
  Active = 'active',
  Archived = 'archived',
}

export type SavedReply = {
  id: string;
  title: string;
  content: string;
  updatedAt: string;
  editable: boolean;
};

export enum AssociateOnboardingStatus {
  Onboard = 'onboard',
  Pending = 'pending',
  Declined = 'declined',
}

export type StoreOpeningHours = {
  businessOpen: boolean;
  openToday: boolean;
  openingTime?: string;
  nextOpeningTime?: string;
};

export const currencies = { USD: 'US$', EUR: '€', GBP: '£', AUD: 'A$' };

export type Product = {
  sku: string;
  price: number;
  salePrice?: number;
  thumbnail?: string;
  title: string;
  currency: keyof typeof currencies;
  url: string;
  retailer?: string;
  retailerId?: string;
  images?: string[];
};

export enum ConversationMessageType {
  Text = 'text',
  Video = 'video',
  Photo = 'photo',
  NewLiveChat = 'new-live-chat',
  EndedChat = 'ended-chat',
  AutoEndedChat = 'auto-ended-chat',
  Timestamp = 'timestamp',
  NeedSelected = 'need-selected',
  DepartmentSelected = 'department-selected',
  AssociateTransfer = 'associate-transfer',
  ContactsInvitationSent = 'black-book-form',
  ContactsInvitationAccepted = 'black-book-added',
  Product = 'product',
  InactivityWarning = 'inactivity-warning',
  InactivityEnded = 'inactivity-ended',
}

export type ProductMessageData = {
  currency: keyof typeof currencies;
  image: string;
  price?: number;
  originalPrice: number;
  skuCode: string;
  title: string;
  url: string;
  retailer: string;
};

export type MessageReaction = {
  author: string;
  date: string;
  emoji: string | null;
};

export type ConversationMessageAttributes = {
  type: ConversationMessageType;
  localMessageId?: string;
  heroConversationId: string;
  mediaUrl?: string;
  data: Partial<
    {
      transferredFrom: string;
      transferredTo: string;
      reaction: MessageReaction;
    } & ProductMessageData
  >;
};

export enum ConversationMessageAuthor {
  Customer = 'customer',
  System = 'system',
  Agent = 'agent',
}

export interface HeroConversationMessage
  extends Pick<
    Message,
    | 'author'
    | 'body'
    | 'dateUpdated'
    | 'lastUpdatedBy'
    | 'sid'
    | 'dateCreated'
    | 'type'
  > {
  attributes: ConversationMessageAttributes;
  author: string | ConversationMessageAuthor;
  conversation: Partial<Conversation> & {
    sid: string;
    attributes?: ConversationAttributes;
  };
  index?: number;
}

export type HeroConversationParticipant = {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  lastOnlineTimestamp: number;
  spokenLanguages: string[];
  type: 'customer' | 'agent';
  retailer: string;
  isAgent?: boolean;
};

export type ArchivedHeroConversationAttributes = {
  salesAmount: string;
  salesCount: number;
  lastSaleObjectId: string;
  archivedAt: string;
  saleRead: boolean;
  topRated: boolean;
};

export type HeroConversation = {
  id: string;
  conversationStarter: {
    firstName: string;
    lastOnlineTimestamp: number;
    id: string;
    location: [number, number];
    languages: string[];
    username: string;
  };
  lastMessageContent: string;
  lastReceivedMessage: string;
  twilioConversationId: string;
  currentAgent: {
    id: string;
    username: string;
    firstName: string;
    image?: string;
    retailer: string;
  };
  participants: HeroConversationParticipant[];
  createdAt: string;
  archiveAttributes?: ArchivedHeroConversationAttributes;
  ended: boolean;
};

export enum ConversationStatus {
  Ended = 'ended',
  Live = 'live',
}

export type ConversationAttributes = {
  customerFocused: boolean;
  status: ConversationStatus;
};

export type TwilioConversation = Conversation & {
  attributes: ConversationAttributes;
};

export type UploadedFile = { size: number; url: string; type: string };

//VS Events model
export enum VsAssociateEventType {
  ConversationRequest = 'conversation-request',
  ConversationMissed = 'conversation-missed',
  ConversationDeclined = 'conversation-declined',
  ConversationAlreadyAccepted = 'conversation-already-accepted',
  ConversationJoinRequestCompleted = 'conversation-join-request-completed',
  ConversationAutoEnded = 'conversation-auto-ended',
  ConversationEnded = 'conversation-associate-ended',
}

export type ShopperLastOnlineEventData = {
  lastOnlineTimestamp: number;
};

export type ShopperLastOnlineEventDataDictionary = Record<string, number>;
export interface VsEventMessage {
  roomId: string;
  trigger: string;
  data:
    | VsAssociateEvent
    | VsTransferEvent
    | ShopperEvent
    | HeroConversation
    | StoreOpeningStateEvent
    | ShopperLastOnlineEventData;
}

export type ConversationRequestEvent = VsAssociateEventInterface<
  VsAssociateEventType.ConversationRequest,
  ConversationRequestEventData
>;
export type ConversationMissedEvent = VsAssociateEventInterface<
  VsAssociateEventType.ConversationMissed,
  ConversationMissedEventData
>;
export type ConversationDeclinedEvent = VsAssociateEventInterface<
  VsAssociateEventType.ConversationDeclined,
  ConversationDeclinedEventData
>;
export type ConversationJoinRequestCompletedEvent = VsAssociateEventInterface<
  VsAssociateEventType.ConversationJoinRequestCompleted,
  ConversationJoineRequestCompletedEventData
>;
export type ConversationAlreadyAcceptedEvent = VsAssociateEventInterface<
  VsAssociateEventType.ConversationAlreadyAccepted,
  ConversationAlreadyAcceptedEventData
>;
export type ConversationAutoEndedEvent = VsAssociateEventInterface<
  VsAssociateEventType.ConversationAutoEnded,
  ConversationAutoEndedEventData
>;
export type ConversationEndedEvent = VsAssociateEventInterface<
  VsAssociateEventType.ConversationEnded,
  ConversationEndedEventData
>;

export type VsAssociateEvent =
  | ConversationRequestEvent
  | ConversationMissedEvent
  | ConversationDeclinedEvent
  | ConversationJoinRequestCompletedEvent
  | ConversationAlreadyAcceptedEvent
  | ConversationAutoEndedEvent
  | ConversationEndedEvent;

export interface VsAssociateEventInterface<
  T extends VsAssociateEventType,
  U extends
    | ConversationRequestEventData
    | ConversationMissedEventData
    | ConversationDeclinedEventData
    | ConversationJoineRequestCompletedEventData
    | ConversationAlreadyAcceptedEventData
    | ConversationAutoEndedEventData
    | ConversationEndedEventData,
> {
  id: string;
  retailerId: string;
  associateId: string;
  type: T;
  read: boolean;
  conversation: HeroConversation;
  data: U;
}

export type ConversationEndedEventData = {
  customerId: string;
};

export type ConversationAutoEndedEventData = {
  customerFirstName: string;
  customerId: string;
};

export type ConversationRequestEventData = {
  customerId: string;
  customerName: string;
  departmentName: string;
  missed: number;
  new: boolean;
  numberOfAssociatesInPool: number;
  recurring: boolean;
};

export type ConversationMissedEventData = {
  customerId: string;
  customerName: string;
  departmentName: string;
  missed: number;
  new: boolean;
  numberOfAssociatesInPool: number;
  recurring: boolean;
  subType: 'customer-missed' | 'associate-missed';
};

export type ConversationDeclinedEventData = {
  goUnavailable: boolean;
  maxDecline: boolean;
};

export type ConversationJoineRequestCompletedEventData = {
  claimingTime: number;
  customerId: string;
};

export type ConversationAlreadyAcceptedEventData = {
  associateName: string;
  customerId: string;
  new: boolean;
};

//transfer events
export enum TransferOutcome {
  ACCEPTED = 'accepted',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  DECLINED = 'declined',
}

export type StoreOpeningStateEvent = {
  storeId: string;
};

export type VsTransferEvent = {
  id: string;
  requested: string[];
  declined: string[];
  conversation: string;
  creator: string;
  note: string;
  retailerId: string;
  open: boolean;
  creation: string;
  expiration: string;
  outcome?: TransferOutcome;
  accepted?: string;
};

export enum OutgoingTransferFormErrors {
  ALL_ASSOCIATES_OFFLINE = 'allRequestedAssociatesOffline',
}

//shopper events
export enum ShopperEventType {
  ProductView = 'product-view',
  BasketCheckout = 'basket-checkout',
  BasketAdd = 'basket-add',
  BasketDelete = 'basket-delete',
  CategoryView = 'category-view',
  Search = 'search',
}

type BasketEventsData = {
  basket: {
    product: Product;
    quantity: number;
  };
};

type ProductViewEventData = {
  product: Product;
};

type CategoryViewEventData = {
  location: string;
  value: string;
};

type TransationData = {
  transaction: {
    type: string;
    order_id: string;
    currency: string;
    subtotal: number;
    total: number;
    line_items: [
      {
        product: Product;
        quantity: number;
        subtotal: number;
      },
    ];
  };
};

type ShopperEventDataMapper = {
  [ShopperEventType.BasketAdd]: BasketEventsData;
  [ShopperEventType.BasketCheckout]: TransationData;
  [ShopperEventType.BasketDelete]: BasketEventsData;
  [ShopperEventType.ProductView]: ProductViewEventData;
  [ShopperEventType.CategoryView]: CategoryViewEventData;
  [ShopperEventType.Search]: CategoryViewEventData;
};

type TShopperEvent<T extends ShopperEventType> = {
  id: string;
  time: string;
  type: T;
  data: ShopperEventDataMapper[T];
};

export type ProductViewShopperEvent =
  TShopperEvent<ShopperEventType.ProductView>;
export type CategoryViewShopperEvent =
  TShopperEvent<ShopperEventType.CategoryView>;
export type SearchShopperEvent = TShopperEvent<ShopperEventType.Search>;
export type BasketAddRemoveEvent =
  | TShopperEvent<ShopperEventType.BasketAdd>
  | TShopperEvent<ShopperEventType.BasketDelete>;
export type BasketCheckoutEvent =
  TShopperEvent<ShopperEventType.BasketCheckout>;

export type ShopperEvent =
  | ProductViewShopperEvent
  | CategoryViewShopperEvent
  | BasketAddRemoveEvent
  | SearchShopperEvent
  | BasketCheckoutEvent;

export enum ShopperEventCategory {
  RECENTLY_VIEWED = 'recently-viewed',
  BASKET_ADDED_REMOVED = 'basket-add-remove',
  PURCHASED = 'purchased',
}
export const mapShopperEventTypeToCategory = (event: ShopperEvent) => {
  if (
    [
      ShopperEventType.Search,
      ShopperEventType.CategoryView,
      ShopperEventType.ProductView,
    ].includes(event.type)
  ) {
    return ShopperEventCategory.RECENTLY_VIEWED;
  }

  if (
    [ShopperEventType.BasketAdd, ShopperEventType.BasketDelete].includes(
      event.type,
    )
  ) {
    return ShopperEventCategory.BASKET_ADDED_REMOVED;
  }

  return ShopperEventCategory.PURCHASED;
};

export type SmartResponse = {
  _id: string;
  smartResponse: string;
};
